import { useState, useEffect } from 'react';
import { fetchClientConfig } from '../api';

/**
 * Custom hook to securely fetch and manage client configuration
 * @param {string} userIdentifier - Unique identifier for the user (for encryption key derivation)
 * @param {boolean} autoFetch - Whether to automatically fetch config on mount
 * @returns {object} - { config, loading, error, refetch }
 */
export const useSecureConfig = (userIdentifier = 'default', autoFetch = true) => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchConfig = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await fetchClientConfig(userIdentifier);
            
            if (result.success) {
                setConfig(result.config);
                
                // Optionally store in localStorage with encryption for offline use
                // Note: Be careful with localStorage as it's not as secure as memory
                if (typeof Storage !== 'undefined') {
                    const encryptedConfig = btoa(JSON.stringify({
                        config: result.config,
                        timestamp: Date.now(),
                        userIdentifier
                    }));
                    localStorage.setItem('secure_client_config', encryptedConfig);
                }
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch configuration');
        } finally {
            setLoading(false);
        }
    };

    const refetch = () => {
        fetchConfig();
    };

    const clearConfig = () => {
        setConfig(null);
        setError(null);
        if (typeof Storage !== 'undefined') {
            localStorage.removeItem('secure_client_config');
        }
    };

    // Load from localStorage on mount as fallback
    useEffect(() => {
        if (typeof Storage !== 'undefined') {
            const storedConfig = localStorage.getItem('secure_client_config');
            if (storedConfig) {
                try {
                    const decoded = JSON.parse(atob(storedConfig));
                    const timeDiff = Date.now() - decoded.timestamp;
                    
                    // Use stored config if it's less than 30 minutes old and for same user
                    if (timeDiff < 30 * 60 * 1000 && decoded.userIdentifier === userIdentifier) {
                        setConfig(decoded.config);
                        console.log('Using cached secure configuration');
                    } else {
                        // Clear old config
                        localStorage.removeItem('secure_client_config');
                    }
                } catch (err) {
                    console.warn('Failed to parse stored config:', err);
                    localStorage.removeItem('secure_client_config');
                }
            }
        }
    }, [userIdentifier]);

    // Auto-fetch on mount if enabled and no config available
    useEffect(() => {
        if (autoFetch && !config && !loading) {
            // Start fetching immediately, don't wait
            fetchConfig();
        }
    }, [userIdentifier, autoFetch, config, loading]);

    return {
        config,
        loading,
        error,
        refetch,
        clearConfig
    };
};

export default useSecureConfig;